import { useCallback, useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import { supabase } from "@/lib/supabaseClient";
import { getPayrollDataById } from "@/hooks/payrollDataService";
import { processPayrollData } from "@/modules/underwriting/pages/payrollProcessor";
import { dealToFlatMap } from "@/modules/underwriting/pages/Dealtoflatmap";

interface TemplateMapping {
  key: string;
  sheet: string;
  cell: string;
  format: "text" | "number" | "currency" | "percent";
  label?: string;
  section?: string;
}

// ---------------------------------------------------------------------------
// decodeFileData
// ---------------------------------------------------------------------------

function decodeFileData(fileData: unknown): ArrayBuffer {
  if (fileData instanceof ArrayBuffer) return fileData;
  if (fileData instanceof Uint8Array) return fileData.buffer as ArrayBuffer;

  if (typeof fileData === "string") {
    if (fileData.startsWith("data:application/")) {
      const base64Part = fileData.split(",")[1];
      if (!base64Part) throw new Error("Invalid base64 data URL");
      const binary = atob(base64Part);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return bytes.buffer as ArrayBuffer;
    }
    if (fileData.startsWith("\\x")) {
      const hex = fileData.substring(2);
      if (hex.startsWith("646174613a6170706c69636174696f6e2f")) {
        let str = "";
        for (let i = 0; i < hex.length; i += 2)
          str += String.fromCharCode(parseInt(hex.substring(i, i + 2), 16));
        return decodeFileData(str);
      }
      const bytes = new Uint8Array(hex.length / 2);
      for (let i = 0; i < bytes.length; i++)
        bytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
      return bytes.buffer as ArrayBuffer;
    }
    try {
      const binary = atob(fileData);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return bytes.buffer as ArrayBuffer;
    } catch {
      /* not base64 */
    }
    throw new Error(`Cannot decode file_data. Length: ${fileData.length}`);
  }

  if (Array.isArray(fileData))
    return new Uint8Array(fileData as number[]).buffer as ArrayBuffer;

  throw new Error(`Unsupported file_data type: ${typeof fileData}`);
}

// ---------------------------------------------------------------------------
// Shared formula expansion — через DOMParser (встроен в браузер, не ломает XML)
// ---------------------------------------------------------------------------

function colToNum(col: string): number {
  let n = 0;
  for (let i = 0; i < col.length; i++) n = n * 26 + col.charCodeAt(i) - 64;
  return n;
}

function numToCol(n: number): string {
  let col = "";
  while (n > 0) {
    col = String.fromCharCode(65 + ((n - 1) % 26)) + col;
    n = Math.floor((n - 1) / 26);
  }
  return col;
}

function parseRef(ref: string): { col: number; row: number } {
  const m = ref.match(/^(\$?)([A-Z]+)(\$?)(\d+)$/);
  if (!m) throw new Error(`Bad ref: ${ref}`);
  return { col: colToNum(m[2]), row: parseInt(m[4]) };
}

/** Сдвигает все относительные ссылки в формуле на (dRow, dCol). */
function slideFormula(formula: string, dRow: number, dCol: number): string {
  return formula.replace(
    /(\$?)([A-Z]{1,3})(\$?)(\d{1,7})/g,
    (_, dc, col, dr, row) => {
      const newCol = dc === "$" ? col : numToCol(colToNum(col) + dCol);
      const newRow = dr === "$" ? row : String(parseInt(row) + dRow);
      return `${dc}${newCol}${dr}${newRow}`;
    },
  );
}

/**
 * Разворачивает shared formulas в XML одного листа через DOMParser.
 */
function expandSharedFormulasInXml(xml: string): string {
  const parser = new DOMParser();
  const serializer = new XMLSerializer();

  const doc = parser.parseFromString(xml, "application/xml");

  const parseError = doc.querySelector("parsererror");
  if (parseError) {
    console.warn("[expandShared] DOMParser error, skipping sheet");
    return xml;
  }

  const masters = new Map<
    string,
    { col: number; row: number; formula: string }
  >();

  const allCells = doc.querySelectorAll("c");
  allCells.forEach((cell) => {
    const fEl = cell.querySelector("f");
    if (!fEl) return;
    if (fEl.getAttribute("t") !== "shared") return;
    if (!fEl.hasAttribute("ref")) return;

    const si = fEl.getAttribute("si");
    const formula = fEl.textContent ?? "";
    const cellRef = cell.getAttribute("r");
    if (!si || !cellRef) return;

    try {
      const { col, row } = parseRef(cellRef);
      masters.set(si, { col, row, formula });
    } catch {
      /* skip */
    }
  });

  if (masters.size === 0) return xml;

  let changed = false;
  allCells.forEach((cell) => {
    const fEl = cell.querySelector("f");
    if (!fEl) return;
    if (fEl.getAttribute("t") !== "shared") return;
    if (fEl.hasAttribute("ref")) {
      fEl.removeAttribute("t");
      fEl.removeAttribute("si");
      fEl.removeAttribute("ref");
      changed = true;
      return;
    }

    const si = fEl.getAttribute("si");
    const cellRef = cell.getAttribute("r");
    if (!si || !cellRef) return;

    const master = masters.get(si);
    if (!master) return;

    try {
      const { col, row } = parseRef(cellRef);
      const dRow = row - master.row;
      const dCol = col - master.col;
      const formula = slideFormula(master.formula, dRow, dCol);

      fEl.textContent = formula;
      fEl.removeAttribute("t");
      fEl.removeAttribute("si");
      changed = true;
    } catch {
      /* skip */
    }
  });

  if (!changed) return xml;

  let result = serializer.serializeToString(doc);
  result = result.replace(/ xmlns(?::\w+)?="[^"]*"/g, (match, offset) => {
    return offset < 500 ? match : "";
  });

  return result;
}

async function expandSharedFormulasInWorkbook(
  buffer: ArrayBuffer,
): Promise<ArrayBuffer> {
  const zip = await JSZip.loadAsync(buffer);

  const sheetPaths = Object.keys(zip.files).filter((n) =>
    /^xl\/worksheets\/sheet\d+\.xml$/.test(n),
  );

  let expandedCount = 0;
  for (const path of sheetPaths) {
    const xml = await zip.files[path].async("string");
    const expanded = expandSharedFormulasInXml(xml);
    if (expanded !== xml) {
      zip.file(path, expanded);
      expandedCount++;
    }
  }

  console.log(
    `[expandShared] Processed ${sheetPaths.length} sheets, expanded ${expandedCount}`,
  );

  return zip.generateAsync({
    type: "arraybuffer",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  });
}

// ---------------------------------------------------------------------------
// writeCell
// ---------------------------------------------------------------------------

function writeCell(
  ws: ExcelJS.Worksheet,
  cellRef: string,
  value: unknown,
  format: TemplateMapping["format"],
  mappingKey?: string,
) {
  if (value == null || value === "") return;

  try {
    const cell = ws.getCell(cellRef);
    const sheetName = ws.name;

    const isRampCell =
      sheetName === "Ramp & Macro" &&
      ["C5", "D5", "E5", "F5", "C9", "D9", "E9", "F9"].includes(cellRef);

    const isPayrollSocialCharges =
      mappingKey != null && mappingKey.includes("socialCharges");

    const rawValue = (cell as any)._value;
    const hasFormula =
      rawValue?.type === "formula" || typeof rawValue?.formula === "string";

    if (hasFormula) {
      cell.value = null;
    }

    if (format === "text") {
      cell.value = String(value);
    } else {
      const num = typeof value === "number" ? value : Number(value);
      if (!isNaN(num)) {
        cell.value =
          isRampCell || isPayrollSocialCharges
            ? num
            : format === "percent"
              ? num / 100
              : num;
      }
    }
  } catch (error) {
    console.error(`[writeCell] Error writing to ${cellRef}:`, error);
  }
}

// ---------------------------------------------------------------------------
// clearCachedValues — убирает кэшированные результаты формул по всему воркбуку.
// Excel/Google Sheets при открытии не найдут старый кэш и пересчитают с нуля.
// ---------------------------------------------------------------------------

function clearCachedValues(wb: ExcelJS.Workbook): void {
  wb.eachSheet((ws) => {
    ws.eachRow((row) => {
      row.eachCell({ includeEmpty: false }, (cell) => {
        const rawValue = (cell as any)._value;
        const hasFormula =
          rawValue?.type === "formula" || typeof rawValue?.formula === "string";
        if (hasFormula && rawValue?.result !== undefined) {
          rawValue.result = undefined;
        }
      });
    });
  });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useExcelExport() {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportDealToExcel = useCallback(async (deal: any) => {
    setIsExporting(true);
    setError(null);

    try {
      // 1. Payroll
      let payrollDetails = null;
      const payrollDataId = deal?.kpis?.payroll?.dataId;
      if (payrollDataId) {
        try {
          const payrollData = await getPayrollDataById(payrollDataId);
          if (payrollData) payrollDetails = processPayrollData(payrollData);
        } catch (e) {
          console.error("[payroll] Error:", e);
        }
      }

      // 2. Шаблон из Supabase
      const { data, error: fetchError } = await supabase
        .from("excel_templates")
        .select("*")
        .eq("is_active", true)
        .order("version", { ascending: false })
        .limit(1)
        .single();

      if (fetchError) throw new Error(`Supabase: ${fetchError.message}`);
      if (!data) throw new Error("No active Excel template found.");

      const rawBuffer = decodeFileData(data.file_data);
      if (rawBuffer.byteLength < 1000)
        throw new Error(
          `Template too small (${rawBuffer.byteLength} bytes). Please re-upload.`,
        );

      // 3. Разворачиваем shared formulas через DOMParser
      const buffer = await expandSharedFormulasInWorkbook(rawBuffer);

      // 4. Загружаем в ExcelJS
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(buffer);
      wb.calcProperties.fullCalcOnLoad = true;

      // 5. Flat map
      const flat = dealToFlatMap(deal, payrollDetails);

      // 6. Пишем значения
      let written = 0;
      let failed = 0;
      const missingKeys: string[] = [];

      for (const entry of data.mapping ?? []) {
        try {
          if (!(entry.key in flat)) {
            missingKeys.push(entry.key);
            failed++;
            continue;
          }
          const ws = wb.getWorksheet(entry.sheet);
          if (!ws) {
            console.warn(`[export] Worksheet not found: ${entry.sheet}`);
            failed++;
            continue;
          }
          writeCell(ws, entry.cell, flat[entry.key], entry.format, entry.key);
          written++;
        } catch (e) {
          console.error(`[export] Error writing ${entry.key}:`, e);
          failed++;
        }
      }

      console.log(`[export] Written: ${written}, Failed: ${failed}`);
      if (missingKeys.length > 0)
        console.warn(
          `[export] Missing keys (${missingKeys.length}):`,
          missingKeys.slice(0, 20),
        );

      // 6.5. Очищаем все кэшированные значения — Excel пересчитает с нуля
      clearCachedValues(wb);

      // 7. Сохраняем
      const outBuffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([outBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const safeName = (deal.name ?? "Hotel").replace(/[^\w\s\-]/g, "").trim();
      saveAs(blob, `${safeName} – Model.xlsx`);
    } catch (err: any) {
      console.error("=".repeat(80));
      console.error("❌ EXPORT FAILED:", err);
      console.error("=".repeat(80));
      const msg = err?.message ?? "Export failed";
      setError(msg);
      throw new Error(msg);
    } finally {
      setIsExporting(false);
    }
  }, []);

  return { exportDealToExcel, isExporting, error };
}
